// frontend/src/components/dashboard/ModalBusqueda.jsx
// MODAL PARA BUSCAR EQUIPOS EN LA BASE DE DATOS REAL
// BASADO EN LA FUNCIONALIDAD DEL SISTEMA ORIGINAL

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// OVERLAY DEL MODAL
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.overlayBackdrop};
  display: ${({ $show }) => $show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

// DIALOG CONTAINER
const ModalDialog = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 1.75rem auto;
`;

// MODAL CONTENT
const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.bgPrincipal};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
`;

// MODAL HEADER
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
`;

const ModalTitle = styled.h5`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.overlayOnPrimarySoft};
  }
`;

// MODAL BODY
const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
`;

// CONTENEDOR DE BÚSQUEDA
const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 15px;
  border: 2px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.txtSecondary};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

// CONTENEDOR DE RESULTADOS
const ResultsContainer = styled.div`
  min-height: 200px;
`;

const InlineError = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  text-align: center;
  padding: 10px;
`;

const ResultsHeader = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-style: italic;
`;

const ResultsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const ResultItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const EquipoCode = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.875rem;
  font-weight: 600;
  margin-right: 1rem;
  min-width: 80px;
  text-align: center;
`;

const EquipoInfo = styled.div`
  flex: 1;
`;

const EquipoTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  margin-bottom: 0.125rem;
  line-height: 1.2;
`;

const EquipoDetails = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  line-height: 1.3;
  
  i {
    margin-right: 0.25rem;
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

// COMPONENTE PRINCIPAL - MODAL DE BÚSQUEDA CON DATOS REALES
const ModalBusqueda = ({ show, onClose, onSelectEquipo, equiposFiltrados }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEquipos, setFilteredEquipos] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // USAR EQUIPOS FILTRADOS EN LUGAR DE CARGAR TODOS
  useEffect(() => {
    if (!show || !equiposFiltrados) {
      setFilteredEquipos([]);
      return;
    }
    
    console.log('🔍 Usando equipos filtrados para búsqueda:', equiposFiltrados.length);
    setFilteredEquipos(equiposFiltrados);
    setError(null);
  }, [show, equiposFiltrados]);

  // FILTRAR EQUIPOS SEGÚN EL TÉRMINO DE BÚSQUEDA
  useEffect(() => {
    if (!equiposFiltrados || !Array.isArray(equiposFiltrados)) {
      setFilteredEquipos([]);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredEquipos(equiposFiltrados);
    } else {
      const filtered = equiposFiltrados.filter(equipo => {
        const searchLower = searchTerm.toLowerCase();
        return (
          equipo.interno?.toLowerCase().includes(searchLower) ||
          equipo.interno_alternativo?.toLowerCase().includes(searchLower) ||
          equipo.descripcion?.toLowerCase().includes(searchLower) ||
          equipo.familia_nombre?.toLowerCase().includes(searchLower) ||
          equipo.marca_nombre?.toLowerCase().includes(searchLower) ||
          equipo.modelo_nombre?.toLowerCase().includes(searchLower) ||
          equipo.sucursal_nombre?.toLowerCase().includes(searchLower) ||
          equipo.patente?.toLowerCase().includes(searchLower) ||
          equipo.chasis?.toLowerCase().includes(searchLower) ||
          equipo.motor?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredEquipos(filtered);
      console.log('🔍 Búsqueda aplicada:', searchTerm, '- Resultados:', filtered.length, 'de', equiposFiltrados.length);
    }
  }, [searchTerm, equiposFiltrados]);

  const handleSelectEquipo = (equipo) => {
    console.log('🎯 Equipo seleccionado:', equipo);
    if (onSelectEquipo) {
      onSelectEquipo(equipo);
    }
    onClose();
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (!show) return null;

  return (
    <ModalOverlay $show={show}>
      <ModalDialog>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              <i className="bi bi-search"></i> Buscar Equipos
            </ModalTitle>
            <CloseButton type="button" onClick={onClose}>
              <i className="bi bi-x-lg"></i>
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {/* CAMPO DE BÚSQUEDA */}
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Buscar por código, descripción, marca, modelo, patente, chasis, motor..."
                value={searchTerm}
                onChange={handleInputChange}
                autoFocus
              />
              <SearchIcon>
                <i className="bi bi-search"></i>
              </SearchIcon>
            </SearchContainer>

            {/* RESULTADOS */}
            <ResultsContainer>
              {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  🔄 Cargando equipos...
                </div>
              )}
              
              {error && (
                <InlineError>
                  ❌ {error}
                </InlineError>
              )}

              {!loading && !error && (
                <>
                  <ResultsHeader>
                    {filteredEquipos.length} equipos encontrados
                    {searchTerm && ` para "${searchTerm}"`}
                    {!searchTerm && equiposFiltrados && equiposFiltrados.length > 0 && 
                      ` (buscando solo en equipos filtrados)`}
                  </ResultsHeader>
                  
                  {/* MENSAJE CUANDO NO HAY EQUIPOS FILTRADOS */}
                  {!equiposFiltrados || equiposFiltrados.length === 0 ? (
                    <NoResults>
                      No hay equipos disponibles para buscar. 
                      {!equiposFiltrados ? ' Cargando...' : ' Aplica filtros para ver equipos.'}
                    </NoResults>
                  ) : (
                    <>
                      {/* MENSAJE CUANDO LA BÚSQUEDA NO ENCUENTRA NADA */}
                      {filteredEquipos.length === 0 && searchTerm && (
                        <NoResults>
                          No se encontraron equipos que coincidan con tu búsqueda 
                          entre los {equiposFiltrados.length} equipos filtrados.
                        </NoResults>
                      )}
                      
                      {/* LISTA DE RESULTADOS */}
                      <ResultsList>
                        {filteredEquipos.map(equipo => (
                          <ResultItem 
                            key={equipo.id} 
                            onClick={() => handleSelectEquipo(equipo)}
                          >
                            <EquipoCode>{equipo.interno || `EQ-${equipo.id}`}</EquipoCode>
                            <EquipoInfo>
                              <EquipoTitle>{equipo.descripcion}</EquipoTitle>
                              <EquipoDetails>
                                {equipo.marca_nombre && (
                                  <>
                                    <i className="bi bi-truck"></i> {equipo.marca_nombre}
                                  </>
                                )}
                                {equipo.modelo_nombre && ` ${equipo.modelo_nombre}`}
                                {equipo.estado_nombre && (
                                  <>
                                    {equipo.marca_nombre && ' • '}
                                    <i className="bi bi-gear"></i> {equipo.estado_nombre}
                                  </>
                                )}
                                {equipo.patente && (
                                  <>
                                    {(equipo.marca_nombre || equipo.estado_nombre) && ' • '}
                                    <i className="bi bi-card-text"></i> {equipo.patente}
                                  </>
                                )}
                                {equipo.chasis && (
                                  <>
                                    {(equipo.marca_nombre || equipo.estado_nombre || equipo.patente) && ' • '}
                                    <i className="bi bi-wrench"></i> {equipo.chasis}
                                  </>
                                )}
                                {equipo.motor && (
                                  <>
                                    {(equipo.marca_nombre || equipo.estado_nombre || equipo.patente || equipo.chasis) && ' • '}
                                    <i className="bi bi-cpu"></i> {equipo.motor}
                                  </>
                                )}
                              </EquipoDetails>
                            </EquipoInfo>
                          </ResultItem>
                        ))}
                      </ResultsList>
                    </>
                  )}
                </>
              )}
            </ResultsContainer>
          </ModalBody>
        </ModalContent>
      </ModalDialog>
    </ModalOverlay>
  );
};

export default ModalBusqueda;
